import type { ReactNode } from 'react';

import { cn } from '../lib/cn';
import { ApiError, NetworkError } from '../api/errors';

const Frame = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center gap-4 rounded-card border border-border px-6 py-8 text-center',
      className,
    )}
  >
    {children}
  </div>
);

interface IEmptyStateProps {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}

export const EmptyState = ({ title, description, action }: IEmptyStateProps) => (
  <Frame>
    <p className="text-section font-semibold text-primary">{title}</p>
    {description && <p className="max-w-form text-body text-muted">{description}</p>}
    {action}
  </Frame>
);

export const NoMatchState = ({ onReset }: { onReset?: () => void }) => (
  <Frame>
    <p className="text-section font-semibold text-primary">Nothing matched the filter</p>
    <p className="max-w-form text-body text-muted">
      There is data here, but no row satisfies every condition at once.
    </p>
    {onReset && (
      <button type="button" onClick={onReset} className="text-body text-accent underline">
        Clear the filter
      </button>
    )}
  </Frame>
);

export const LoadingState = ({ rows = 8, className }: { rows?: number; className?: string }) => (
  <div className={cn('flex flex-col gap-1', className)} aria-busy="true" aria-live="polite">
    {Array.from({ length: rows }, (_, index) => (
      <div
        key={index}
        className="h-row animate-[hp-pulse_1.8s_ease-in-out_infinite] rounded-control border border-border bg-surface-raised"
      />
    ))}
  </div>
);

export const ErrorState = ({ error, onRetry }: { error: unknown; onRetry?: () => void }) => {
  const isNetwork = error instanceof NetworkError;
  const api = error instanceof ApiError ? error : null;

  return (
    <Frame className="border-danger">
      <p className="text-section font-semibold text-danger-text">
        {isNetwork ? 'Could not reach it' : 'That did not work'}
      </p>

      <p className="max-w-form text-body text-primary">
        {api?.message ?? (isNetwork ? 'The request never left. Nothing was saved.' : String(error))}
      </p>

      {api && api.fieldErrors.length > 0 && (
        <ul className="max-w-form text-left text-meta text-muted">
          {api.fieldErrors.map((fieldError) => (
            <li key={`${fieldError.field}:${fieldError.message}`}>
              <span className="font-mono text-primary">{fieldError.field}</span> -{' '}
              {fieldError.message}
            </li>
          ))}
        </ul>
      )}

      {api && (
        <p className="font-mono text-meta text-faint">
          {api.status} · {api.code}
          {api.requestId && ` · ${api.requestId}`}
        </p>
      )}

      {onRetry && (
        <button type="button" onClick={onRetry} className="text-body text-accent underline">
          Try again
        </button>
      )}
    </Frame>
  );
};

export const ForbiddenState = ({ needs }: { needs?: string }) => (
  <Frame>
    <p className="text-section font-semibold text-primary">Your role cannot open this</p>
    <p className="max-w-form text-body text-muted">
      {needs
        ? `This section is for the ${needs} role. Ask an owner of the organization to change yours.`
        : 'Ask an owner of the organization to change your role.'}
    </p>
  </Frame>
);
